import { Anchor, denormalizeRect, type NormalizedRect } from '@st/geom'
import {
  child,
  defModule,
  Task,
  Update,
  type Module,
  type RuntimeDefectReporter,
  type UpdateResult
} from '@st/redux'
import { deepEqual } from '@st/util/deep-equal'
import { jsonMapDiff } from '@st/util/json-diff'
import { Effect, Layer, Stream } from 'effect'
import type { Service } from 'effect/Context'
import type {
  DocumentViewerAnyPluginMessage,
  DocumentViewerDefinition,
  DocumentViewerMessage,
  DocumentViewerPluginLifecycleMessage,
  DocumentViewerPluginRuntimeContext,
  DocumentViewerPluginStateModule
} from './document-viewer-types'
import type { DocumentViewerCommand } from './document-viewer-command'
import {
  selViewerCssPxPerPdfPt,
  DocumentViewerCoreMessage,
  DocumentViewerState,
  makeDocumentViewerModule,
  selViewerPageTransform,
  selViewerZoomLevel
} from './document-viewer-module'
import { loadViewportSettingsEffect } from './document-viewer-viewport-module'
import type { PageStructure, ResolvedPageStructure, ViewerPage } from './models'
import type { DocumentLocation } from './page-layers'
import {
  type DocumentRendererEvent,
  type DocumentRendererRenderMode,
  type DocumentRendererService
} from './document-viewer-services'
import { ViewportService } from './viewport-service'
import type { ViewportMeasurement, ViewportScrollTarget } from './viewport-service'
import {
  resolvePageRects,
  selPageTransform,
  selScrollDocumentLocationRect,
  type PageRect,
  type ViewerViewportState
} from './viewer-geometry'

type DocumentRendererTag = Service<any, DocumentRendererService<unknown, ViewerPage>>

type ComposedViewerState<Document, _PluginsInput extends Record<string, unknown>> = {
  viewer: DocumentViewerState
  document: Document | null
  rawPages: PageStructure[]
  pagesStatus: 'loading' | 'loaded' | 'failed'
  pages: ResolvedPageStructure[]
  pageLayoutVersion: number
  pluginStates: Record<string, unknown>
  navigation: NavigationRecord | undefined
  rendererSessionId: string | null
  rendererRenderMode: DocumentRendererRenderMode
  viewportState: ViewerViewportState
  pageRenderStateByPageNumber: Record<number, PageRenderState>
}

/**
 * The annotation selection a navigation establishes: select one annotation,
 * clear the selection, or leave it alone. It is a one-shot part of the intent,
 * not desired state: once established, the user's own selection changes win.
 */
export type ViewerSelection = 'keep' | 'clear' | { annotationId: string }

/**
 * Where a navigation scrolls: a page, optionally a region on it. `bounds` are
 * page points (`DocumentLocation`); `pageRegion` is a fraction of the page box
 * as the viewer lays it out (after rotation, and collapsed for deleted pages),
 * which the viewer converts once that page exists. Either way the caller never
 * reads renderer state to place a destination.
 */
export type ViewerDestination =
  | DocumentLocation
  | { pageNumber: number; pageRegion: NormalizedRect }

/**
 * The caller's latest navigation intent for the presented document. The token
 * names the intent: a new token is a new request (even to the same place), and
 * presenting the same token again only retargets it while it is still pending.
 *
 * `location` is where to scroll. `awaiting` holds the intent, and cancels an
 * older one, while the caller's own data cannot place it yet (renderer
 * readiness is the viewer's concern, never the caller's); `null` means there is
 * nothing to scroll, so the intent settles as soon as its selection step ran.
 * A page region on a page the laid-out document does not have is a caller bug;
 * an absolute location there simply has no scroll target.
 */
export type ViewerNavigation = {
  token: number
  location: ViewerDestination | 'awaiting' | null
  selection: ViewerSelection
  behavior: ScrollBehavior
}

/**
 * Everything the caller shows in the viewer, applied atomically in a fixed
 * order: document, initial zoom, annotations, then navigation. Callers present
 * the whole projection whenever it may have changed; unchanged parts are no-ops.
 */
export type ViewerPresentation<Document> = {
  document: Document | null
  annotations: Record<string, unknown>
  /** Absent intent cancels any navigation still in flight. */
  navigation: ViewerNavigation | null
  /** Applied when the presented document identity changes. */
  initialZoom: number | undefined
}

/**
 * Viewer-owned lifecycle of the latest navigation intent.
 *
 *   present(token 7, awaiting, select c9)  -> pending; selects c9 once c9 is
 *                                             presented, nothing to scroll yet
 *   present(token 7, page 3)               -> pending; the navigation Task scrolls
 *   present(token 7, page 4)               -> pending, retargeted before settling
 *   navigationSettled(7)                   -> settled: the viewport landed
 *   navigationTakenOver(7)                 -> settled: the user scrolled the viewport
 *                                             while 7 was pending, even before layout
 *   present(token 7, page 5)               -> ignored: a settled intent never replays
 *   requestViewerScroll                    -> settled (a direct jump supersedes it)
 *   present(token 8, ...)                  -> replaces 7, whatever its status
 *   present(navigation: null)              -> cleared
 *
 * Selection is established while the intent is pending, as soon as the
 * annotations it names are presented, and at most once: `owed` until then,
 * `established` after, `done` when it can no longer apply. A same-document
 * source revision reinitializes plugin state and so drops the selection; it is
 * re-established only if the user had not changed it in the meantime.
 *
 * Identity is the document the intent was presented with. It is null while the
 * caller presents no document (for example while that document's layout is
 * still loading) and binds on the first document presented with the token.
 * Presenting a bound token with another document is a caller bug.
 */
type NavigationRecord = ViewerNavigation & {
  identity: string | null
  status: 'pending' | 'settled'
  selectionStatus: 'owed' | 'established' | 'done'
}

export type PageRenderState = {
  pageTextId: string | null
  error: string | null
}

const IDLE_PAGE_RENDER_STATE: PageRenderState = {
  pageTextId: null,
  error: null
}

const EMPTY_VIEWER_VIEWPORT_STATE: ViewerViewportState = {
  pagesToRender: [],
  activePageNumber: null
}

function isPageRenderStateEmpty(pageRenderState: PageRenderState) {
  return pageRenderState.pageTextId == null && pageRenderState.error == null
}

function withPageRenderState(
  pageRenderStateByPageNumber: Record<number, PageRenderState>,
  pageNumber: number,
  pageRenderState: PageRenderState
) {
  if (isPageRenderStateEmpty(pageRenderState)) {
    const nextPageRenderStateByPageNumber = {
      ...pageRenderStateByPageNumber
    }
    delete nextPageRenderStateByPageNumber[pageNumber]
    return nextPageRenderStateByPageNumber
  }

  return {
    ...pageRenderStateByPageNumber,
    [pageNumber]: pageRenderState
  }
}

type ComposedViewerMessage<Document, _PluginsInput extends Record<string, unknown>> =
  | { type: 'present'; presentation: ViewerPresentation<Document> }
  | { type: 'navigationSettled'; token: number }
  | { type: 'navigationTakenOver'; token: number }
  | { type: 'viewer'; message: DocumentViewerMessage }
  | {
      type: 'pluginStates'
      sessionKey: string | undefined
      message: DocumentViewerMessage
    }
  | { type: 'viewerCommand'; command: DocumentViewerCommand }
  | { type: 'viewportMeasured'; measurement: ViewportMeasurement }
  | { type: 'viewportStateChanged'; viewportState: ViewerViewportState }
  | {
      type: 'rendererSessionOpened'
      sessionId: string
      pages: PageStructure[]
      renderMode?: DocumentRendererRenderMode
    }
  | { type: 'rendererSessionOpenFailed'; error: string }
  | {
      type: 'rendererPageRendered'
      render: {
        sessionId: string
        pageNumber: number
      }
    }
  | {
      type: 'rendererPageTextExtracted'
      sessionId: string
      pageNumber: number
      pageTextId: string
    }
  | {
      type: 'rendererPageRenderFailed'
      sessionId: string
      pageNumber: number
      error: string
    }

function toSerializableErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message
  }
  return String(error)
}

function resolveDocumentRuntimeContext(
  documentSource: unknown
): DocumentViewerPluginRuntimeContext {
  if (typeof documentSource != 'object' || documentSource == null) {
    return {}
  }

  const sessionKey =
    'sessionKey' in documentSource &&
    typeof (documentSource as { sessionKey?: unknown }).sessionKey == 'string'
      ? (documentSource as { sessionKey: string }).sessionKey
      : undefined
  const documentId =
    'documentId' in documentSource &&
    typeof (documentSource as { documentId?: unknown }).documentId == 'string'
      ? (documentSource as { documentId: string }).documentId
      : undefined

  return { sessionKey, documentId }
}

function fingerprintDocumentSource(source: unknown): bigint {
  const serialized = JSON.stringify(source)
  if (serialized == undefined) {
    throw new Error('Document viewer source must be serializable')
  }

  // Task arguments appear in diagnostics. Keep signed source URLs out of those
  // names while retaining deterministic identity for source replacement.
  let fingerprint = 0xcbf29ce484222325n
  for (let index = 0; index < serialized.length; index += 1) {
    fingerprint ^= BigInt(serialized.charCodeAt(index))
    fingerprint = BigInt.asUintN(64, fingerprint * 0x100000001b3n)
  }
  return fingerprint
}

export function selPageRenderStateByPageNumber(
  pageRenderStateByPageNumber: Record<number, PageRenderState>,
  pageNumber: number
) {
  return pageRenderStateByPageNumber[pageNumber] ?? IDLE_PAGE_RENDER_STATE
}

function releasePageTextEffect(documentRendererTag: DocumentRendererTag, pageTextId: string) {
  return Effect.gen(function* () {
    const renderer = yield* documentRendererTag
    yield* renderer.releasePageText(pageTextId)
    return null
  })
}

function setDesiredPagesEffect(
  documentRendererTag: DocumentRendererTag,
  sessionId: string,
  pageNumbers: number[]
) {
  return Effect.gen(function* () {
    const renderer = yield* documentRendererTag
    yield* renderer.setDesiredPages(sessionId, pageNumbers)
    return null
  })
}

function releaseRenderedPageTextEffect({
  documentRendererTag,
  pageRenderStateByPageNumber
}: {
  documentRendererTag: DocumentRendererTag
  pageRenderStateByPageNumber: Record<number, PageRenderState>
}) {
  return Effect.gen(function* () {
    for (const pageRenderState of Object.values(pageRenderStateByPageNumber)) {
      if (pageRenderState.pageTextId) {
        yield* releasePageTextEffect(documentRendererTag, pageRenderState.pageTextId)
      }
    }

    return null
  })
}

function toComposedViewerMessageFromRendererEvent<
  Document,
  PluginsInput extends Record<string, unknown>
>(event: DocumentRendererEvent): ComposedViewerMessage<Document, PluginsInput> {
  if (event.type == 'pageRendered') {
    return {
      type: 'rendererPageRendered',
      render: event.render
    }
  }

  if (event.type == 'pageTextExtracted') {
    return {
      type: 'rendererPageTextExtracted',
      sessionId: event.sessionId,
      pageNumber: event.pageNumber,
      pageTextId: event.pageTextId
    }
  }

  return {
    type: 'rendererPageRenderFailed',
    sessionId: event.sessionId,
    pageNumber: event.pageNumber,
    error: event.error
  }
}

function sequenceSideEffects(effects: Effect.Effect<unknown, never, any>[]) {
  return Effect.all(effects).pipe(Effect.as(null))
}

function bindViewportContainerEffect(containerId: string | null) {
  return Effect.gen(function* () {
    const viewport = yield* ViewportService
    yield* viewport.bind(containerId)
    return null
  })
}

function attachPageSurfaceEffect({
  documentRendererTag,
  sessionId,
  pageNumber,
  surfaceId
}: {
  documentRendererTag: DocumentRendererTag
  sessionId: string
  pageNumber: number
  surfaceId: string
}) {
  return Effect.gen(function* () {
    const renderer = yield* documentRendererTag
    yield* renderer.attachPageSurface(sessionId, pageNumber, surfaceId)
    return null
  })
}

function detachPageSurfaceEffect({
  documentRendererTag,
  sessionId,
  pageNumber,
  surfaceId
}: {
  documentRendererTag: DocumentRendererTag
  sessionId: string
  pageNumber: number
  surfaceId: string
}) {
  return Effect.gen(function* () {
    const renderer = yield* documentRendererTag
    yield* renderer.detachPageSurface(sessionId, pageNumber, surfaceId)
    return null
  })
}

function prepareViewportForDocumentOpen() {
  return Effect.gen(function* () {
    const viewport = yield* ViewportService
    yield* viewport.prepareForDocumentOpen()
    return null
  })
}

function viewportScrollToTargetEffect(target: ViewportScrollTarget, behavior?: ScrollBehavior) {
  return Effect.gen(function* () {
    const viewport = yield* ViewportService
    yield* viewport.scrollToTarget(target, behavior)
    return null
  })
}

function emitNavigationSettledEffect<Document, PluginsInput extends Record<string, unknown>>(
  token: number
) {
  return Effect.succeed({
    type: 'navigationSettled',
    token
  } as ComposedViewerMessage<Document, PluginsInput>)
}

function reconcileNavigationRecord(
  record: NavigationRecord | undefined,
  navigation: ViewerNavigation | null,
  identity: string | null
): NavigationRecord | undefined {
  if (navigation == null) return undefined
  if (record?.token != navigation.token) {
    return {
      ...navigation,
      identity,
      status: 'pending',
      selectionStatus: initialSelectionStatus(navigation.selection)
    }
  }
  if (record.identity != null && identity != null && record.identity != identity) {
    throw new Error(`Navigation ${navigation.token} was presented with another document`)
  }
  // The token is the intent: only its placement can change, and only while it
  // is pending. A settled intent never replays. Callers present on every pass,
  // so an unchanged record is returned as is to keep viewer state stable.
  const next: NavigationRecord = { ...record, identity: identity ?? record.identity }
  if (record.status == 'pending') {
    next.location = navigation.location
    next.behavior = navigation.behavior
  }
  if (deepEqual(next, record)) return record
  return next
}

function settleNavigationRecord(record: NavigationRecord): NavigationRecord {
  if (record.selectionStatus == 'owed')
    return { ...record, status: 'settled', selectionStatus: 'done' }
  return { ...record, status: 'settled' }
}

function initialSelectionStatus(selection: ViewerSelection): NavigationRecord['selectionStatus'] {
  if (selection == 'keep') return 'done'
  return 'owed'
}

type AnnotationsSelectionState = { type: 'none' } | { type: string; annotationId: string }

function selectionMessage(selection: Exclude<ViewerSelection, 'keep'>) {
  if (selection == 'clear') return { type: 'deselectAnnotation' }
  return { type: 'selectAnnotation', annotationId: selection.annotationId }
}

function reestablishedSelectionStatus(
  selection: ViewerSelection,
  shownBeforeRevision: AnnotationsSelectionState
): NavigationRecord['selectionStatus'] {
  if (isSelectionShown(selection, shownBeforeRevision)) return 'owed'
  return 'done'
}

// Whether the annotations plugin currently shows exactly what this selection
// established, i.e. the user has not changed it since.
function isSelectionShown(selection: ViewerSelection, shown: AnnotationsSelectionState): boolean {
  if (selection == 'keep') return false
  if (selection == 'clear') return shown.type == 'none'
  return 'annotationId' in shown && shown.annotationId == selection.annotationId
}

function requestViewportSettings<Document, PluginsInput extends Record<string, unknown>>(
  settingsKey: string
) {
  return Effect.map(loadViewportSettingsEffect(settingsKey), (message) =>
    message ? ({ type: 'viewer', message } as ComposedViewerMessage<Document, PluginsInput>) : null
  )
}

function toViewportMeasuredMessage<Document, PluginsInput extends Record<string, unknown>>(
  state: ComposedViewerState<Document, PluginsInput>,
  measurement: ViewportMeasurement,
  viewportInitialZoomMode: 'actualSize' | 'fitToWidth'
): DocumentViewerCoreMessage {
  return {
    type: 'viewportMeasured',
    containerWidthPx: measurement.containerWidthPx,
    containerHeightPx: measurement.containerHeightPx,
    firstPageWidthPt: state.pages[0]?.bounds.width,
    initialZoomMode: viewportInitialZoomMode,
    cssPxPerPdfPt: measurement.cssPxPerPdfPt
  }
}

function initPluginStates(
  pluginModules: Record<string, DocumentViewerPluginStateModule<unknown, { type: string }>>,
  context: DocumentViewerPluginRuntimeContext
): UpdateResult<Record<string, unknown>, DocumentViewerMessage> {
  let update: UpdateResult<Record<string, unknown>, DocumentViewerMessage> = {}

  for (const [pluginId, pluginModule] of Object.entries(pluginModules)) {
    update = Update.pipe<Record<string, unknown>, DocumentViewerMessage>(update, (pluginStates) =>
      Update.mapState(pluginModule.init(context), (pluginState) => ({
        ...pluginStates,
        [pluginId]: pluginState
      }))
    )
  }

  return update
}

function applyPluginMessageToStateMap(
  pluginStateMap: Record<string, unknown>,
  pluginId: string,
  pluginModule: DocumentViewerPluginStateModule<unknown, { type: string }>,
  message: { type: string } | DocumentViewerPluginLifecycleMessage,
  context: DocumentViewerPluginRuntimeContext
): UpdateResult<Record<string, unknown>, DocumentViewerMessage> {
  const currentPluginState = pluginStateMap[pluginId]

  const pluginResult = pluginModule.update(currentPluginState, message, context)
  if (!Array.isArray(pluginResult)) {
    return {
      ...pluginStateMap,
      [pluginId]: pluginResult
    }
  }

  const [nextPluginState, effect] = pluginResult
  return [
    {
      ...pluginStateMap,
      [pluginId]: nextPluginState
    },
    effect
  ] as const
}

function reducePluginStateMap(
  pluginStateMap: Record<string, unknown>,
  message: DocumentViewerAnyPluginMessage | DocumentViewerPluginLifecycleMessage,
  pluginModules: Record<string, DocumentViewerPluginStateModule<unknown, { type: string }>>,
  context: DocumentViewerPluginRuntimeContext
): UpdateResult<Record<string, unknown>, DocumentViewerMessage> {
  if (message.type == 'sourceWillChange') {
    return Object.entries(pluginModules).reduce<
      UpdateResult<Record<string, unknown>, DocumentViewerMessage>
    >(
      (update, [pluginId, pluginModule]) =>
        Update.pipe(update, (nextPluginStateMap) =>
          applyPluginMessageToStateMap(nextPluginStateMap, pluginId, pluginModule, message, context)
        ),
      pluginStateMap
    )
  }

  const pluginModule = pluginModules[message.pluginId]
  if (!pluginModule) {
    throw new Error(`Unknown document viewer plugin: ${message.pluginId}`)
  }
  return applyPluginMessageToStateMap(
    pluginStateMap,
    message.pluginId,
    pluginModule,
    message.message,
    context
  )
}

function makeComposedDocumentViewerModule<Document, PluginsInput extends Record<string, unknown>>({
  documentRendererTag,
  pluginModules,
  viewportInitialZoomMode,
  definition
}: {
  documentRendererTag: DocumentRendererTag
  pluginModules: Record<string, DocumentViewerPluginStateModule<unknown, { type: string }>>
  viewportInitialZoomMode: 'actualSize' | 'fitToWidth'
  definition: DocumentViewerDefinition<PluginsInput, Document>
}): Module<
  ComposedViewerState<Document, PluginsInput>,
  ComposedViewerMessage<Document, PluginsInput>
> {
  const viewerModule = makeDocumentViewerModule(definition.viewport?.settingsKey)
  const viewer = child(viewerModule, 'viewer')
  const observeRendererSession = Task.define(
    (input: { key: bigint; source: unknown }) =>
      Stream.unwrap(
        Effect.gen(function* () {
          const renderer = yield* documentRendererTag
          yield* prepareViewportForDocumentOpen()
          const opened = yield* Effect.acquireRelease(renderer.open(input.source), (session) =>
            renderer.close(session.sessionId)
          )
          const events = yield* renderer.events(opened.sessionId)

          return Stream.concat(
            Stream.succeed({
              type: 'rendererSessionOpened',
              sessionId: opened.sessionId,
              pages: opened.pages as PageStructure[],
              renderMode: opened.renderMode
            } as ComposedViewerMessage<Document, PluginsInput>),
            events.pipe(
              Stream.map((event) =>
                toComposedViewerMessageFromRendererEvent<Document, PluginsInput>(event)
              )
            )
          )
        }).pipe(
          Effect.catchTag('DocumentRendererOpenError', (error) =>
            Effect.succeed(
              Stream.succeed({
                type: 'rendererSessionOpenFailed',
                error: toSerializableErrorMessage(error)
              } as ComposedViewerMessage<Document, PluginsInput>)
            )
          )
        )
      ),
    { key: (input) => input.key }
  )
  const observeViewportMeasurements = Task.define((_viewportKey: string) =>
    Stream.unwrap(
      Effect.map(Effect.service(ViewportService), (viewport) =>
        viewport.measurements.pipe(
          Stream.map(
            (measurement): ComposedViewerMessage<Document, PluginsInput> => ({
              type: 'viewportMeasured',
              measurement
            })
          )
        )
      )
    )
  )
  const mapPluginStateUpdate = (
    state: ComposedViewerState<Document, PluginsInput>,
    update: UpdateResult<Record<string, unknown>, DocumentViewerMessage>
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > =>
    Update.map(update, {
      state: (pluginStates) => ({ ...state, pluginStates }),
      message: (message) => ({
        type: 'pluginStates',
        sessionKey: resolveStateDocumentRuntimeContext(state).sessionKey,
        message
      })
    })
  const viewportSettingsKey = definition.viewport?.settingsKey
  const resolveSourceFromDocument = (document: Document) => {
    if (!definition.resolveSource) {
      return document as unknown
    }
    return definition.resolveSource(document)
  }
  const resolveRendererSessionInput = (document: Document) => {
    const source = resolveSourceFromDocument(document)
    return { key: fingerprintDocumentSource(source), source }
  }
  const resolveStateDocumentRuntimeContext = (
    state: ComposedViewerState<Document, PluginsInput>
  ): DocumentViewerPluginRuntimeContext =>
    state.document == null
      ? {}
      : resolveDocumentRuntimeContext(resolveSourceFromDocument(state.document))

  const resetRendererState = (
    state: ComposedViewerState<Document, PluginsInput>
  ): ComposedViewerState<Document, PluginsInput> => ({
    ...state,
    rendererSessionId: null,
    rendererRenderMode: 'bitmap',
    viewportState: EMPTY_VIEWER_VIEWPORT_STATE,
    pageRenderStateByPageNumber: {}
  })

  const clearRenderedAssetsInState = (
    state: ComposedViewerState<Document, PluginsInput>
  ): ComposedViewerState<Document, PluginsInput> => ({
    ...state,
    viewportState: EMPTY_VIEWER_VIEWPORT_STATE,
    pageRenderStateByPageNumber: {}
  })

  const resolvePagesFromRawPages = (
    rawPages: PageStructure[],
    document: Document | null
  ): ResolvedPageStructure[] => {
    if (!definition.resolvePages || document == null) {
      return rawPages as ResolvedPageStructure[]
    }

    return definition.resolvePages(rawPages, document) as ResolvedPageStructure[]
  }

  const resolveViewportStateSubscriptionKey = (
    state: ComposedViewerState<Document, PluginsInput>
  ) => {
    const transform = selViewerPageTransform(state.viewer)

    return [
      'viewport:state',
      state.pagesStatus,
      state.pageLayoutVersion,
      transform.a,
      transform.b,
      transform.c,
      transform.d,
      transform.tx,
      transform.ty
    ].join(':')
  }

  const observeViewportStates = Task.define(
    (input: { key: string; pageRects: PageRect[] }) =>
      Stream.unwrap(
        Effect.map(Effect.service(ViewportService), (viewport) =>
          viewport.viewportStates(input.pageRects).pipe(
            Stream.map(
              (viewportState): ComposedViewerMessage<Document, PluginsInput> => ({
                type: 'viewportStateChanged',
                viewportState
              })
            )
          )
        )
      ),
    { key: (input) => input.key }
  )

  function reapplyViewportMeasurement(
    state: ComposedViewerState<Document, PluginsInput>
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > {
    const viewportMeasurement = state.viewer.viewportMeasurement
    if (!viewportMeasurement) {
      return state
    }

    return reduceViewerMessage(
      state,
      toViewportMeasuredMessage(state, viewportMeasurement, viewportInitialZoomMode)
    )
  }

  const reduceViewportStateChanged = (
    state: ComposedViewerState<Document, PluginsInput>,
    nextViewportState: ViewerViewportState
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    if (state.pagesStatus != 'loaded') {
      return state
    }

    if (
      deepEqual(nextViewportState.pagesToRender, state.viewportState.pagesToRender) &&
      nextViewportState.activePageNumber == state.viewportState.activePageNumber
    ) {
      return state
    }

    const nextPageRenderStateByPageNumber: Record<number, PageRenderState> = {
      ...state.pageRenderStateByPageNumber
    }
    const sideEffects: Effect.Effect<unknown, never, any>[] = []
    const rendererSessionId = state.rendererSessionId
    const renderTargets = new Set(nextViewportState.pagesToRender)

    for (const [pageNumberKey, pageRenderState] of Object.entries(
      nextPageRenderStateByPageNumber
    )) {
      const pageNumber = Number(pageNumberKey)
      if (renderTargets.has(pageNumber)) {
        continue
      }

      if (pageRenderState.pageTextId) {
        sideEffects.push(releasePageTextEffect(documentRendererTag, pageRenderState.pageTextId))
      }

      delete nextPageRenderStateByPageNumber[pageNumber]
    }

    if (state.rendererRenderMode == 'bitmap' && rendererSessionId) {
      sideEffects.push(
        setDesiredPagesEffect(
          documentRendererTag,
          rendererSessionId,
          nextViewportState.pagesToRender
        )
      )
    }

    const nextState: ComposedViewerState<Document, PluginsInput> = {
      ...state,
      viewportState: nextViewportState,
      pageRenderStateByPageNumber: nextPageRenderStateByPageNumber
    }

    if (sideEffects.length == 0) {
      return nextState
    }

    return [
      nextState,
      sequenceSideEffects(sideEffects) as Effect.Effect<null, never, unknown>
    ] as const
  }

  const reduceRendererSessionOpened = (
    state: ComposedViewerState<Document, PluginsInput>,
    message: Extract<
      ComposedViewerMessage<Document, PluginsInput>,
      { type: 'rendererSessionOpened' }
    >
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    if (state.document == null || state.pagesStatus != 'loading' || state.rendererSessionId) {
      throw new Error('Renderer session opened outside the loading phase')
    }

    const resolvedPages = resolvePagesFromRawPages(message.pages, state.document)

    const openedState: ComposedViewerState<Document, PluginsInput> = {
      ...resetRendererState(state),
      rendererSessionId: message.sessionId,
      rendererRenderMode: message.renderMode ?? 'bitmap',
      pagesStatus: 'loaded',
      rawPages: message.pages as PageStructure[],
      pages: resolvedPages,
      pageLayoutVersion: state.pageLayoutVersion + 1
    }

    return Update.pipe(reapplyViewportMeasurement(openedState), (nextState) =>
      reduceViewportStateChanged(nextState, nextState.viewportState)
    )
  }

  const reduceRendererSessionOpenFailed = (
    state: ComposedViewerState<Document, PluginsInput>,
    _message: Extract<
      ComposedViewerMessage<Document, PluginsInput>,
      { type: 'rendererSessionOpenFailed' }
    >
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    if (state.document == null || state.pagesStatus != 'loading' || state.rendererSessionId) {
      throw new Error('Renderer session failed outside the loading phase')
    }

    return {
      ...resetRendererState(state),
      rawPages: [],
      pagesStatus: 'failed',
      pages: []
    } as ComposedViewerState<Document, PluginsInput>
  }

  const reduceRendererPageRendered = (
    state: ComposedViewerState<Document, PluginsInput>,
    message: Extract<
      ComposedViewerMessage<Document, PluginsInput>,
      { type: 'rendererPageRendered' }
    >
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    if (message.render.sessionId != state.rendererSessionId) {
      return state
    }

    if (!state.viewportState.pagesToRender.includes(message.render.pageNumber)) {
      return state
    }

    const currentPageState =
      state.pageRenderStateByPageNumber[message.render.pageNumber] ?? IDLE_PAGE_RENDER_STATE
    if (currentPageState.error == null) {
      return state
    }

    return {
      ...state,
      pageRenderStateByPageNumber: withPageRenderState(
        state.pageRenderStateByPageNumber,
        message.render.pageNumber,
        {
          pageTextId: currentPageState.pageTextId,
          error: null
        }
      )
    }
  }

  const reduceRendererPageTextExtracted = (
    state: ComposedViewerState<Document, PluginsInput>,
    message: Extract<
      ComposedViewerMessage<Document, PluginsInput>,
      { type: 'rendererPageTextExtracted' }
    >
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    if (message.sessionId != state.rendererSessionId) {
      return [state, releasePageTextEffect(documentRendererTag, message.pageTextId)] as const
    }

    const currentPageRenderState = state.pageRenderStateByPageNumber[message.pageNumber]
    if (!state.viewportState.pagesToRender.includes(message.pageNumber)) {
      return [state, releasePageTextEffect(documentRendererTag, message.pageTextId)] as const
    }

    const currentPageState = currentPageRenderState ?? IDLE_PAGE_RENDER_STATE
    if (currentPageState.pageTextId == message.pageTextId) {
      return state
    }

    const nextState: ComposedViewerState<Document, PluginsInput> = {
      ...state,
      pageRenderStateByPageNumber: withPageRenderState(
        state.pageRenderStateByPageNumber,
        message.pageNumber,
        {
          pageTextId: message.pageTextId,
          error: currentPageState.error
        }
      )
    }

    if (!currentPageState.pageTextId) {
      return nextState
    }

    return [
      nextState,
      releasePageTextEffect(documentRendererTag, currentPageState.pageTextId)
    ] as const
  }

  const reduceRendererPageRenderFailed = (
    state: ComposedViewerState<Document, PluginsInput>,
    message: Extract<
      ComposedViewerMessage<Document, PluginsInput>,
      { type: 'rendererPageRenderFailed' }
    >
  ) => {
    if (message.sessionId != state.rendererSessionId) {
      return state
    }

    if (!state.viewportState.pagesToRender.includes(message.pageNumber)) {
      return state
    }

    const currentPageState =
      state.pageRenderStateByPageNumber[message.pageNumber] ?? IDLE_PAGE_RENDER_STATE
    if (currentPageState.error == message.error) {
      return state
    }

    return {
      ...state,
      pageRenderStateByPageNumber: withPageRenderState(
        state.pageRenderStateByPageNumber,
        message.pageNumber,
        {
          pageTextId: currentPageState.pageTextId,
          error: message.error
        }
      )
    }
  }

  const reducePageSurfaceMounted = (
    state: ComposedViewerState<Document, PluginsInput>,
    message: Extract<DocumentViewerMessage, { type: 'pageSurfaceMounted' }>
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    if (!state.rendererSessionId) {
      return state
    }

    return [
      state,
      attachPageSurfaceEffect({
        documentRendererTag,
        sessionId: state.rendererSessionId,
        pageNumber: message.pageNumber,
        surfaceId: message.surfaceId
      })
    ] as const
  }

  const reducePageSurfaceUnmounted = (
    state: ComposedViewerState<Document, PluginsInput>,
    message: Extract<DocumentViewerMessage, { type: 'pageSurfaceUnmounted' }>
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    if (!state.rendererSessionId) {
      return state
    }

    return [
      state,
      detachPageSurfaceEffect({
        documentRendererTag,
        sessionId: state.rendererSessionId,
        pageNumber: message.pageNumber,
        surfaceId: message.surfaceId
      })
    ] as const
  }

  // Pixel target of a document location under the current layout; null while
  // the page is not part of this document.
  const resolveViewportScrollTarget = (
    state: ComposedViewerState<Document, PluginsInput>,
    location: DocumentLocation
  ): ViewportScrollTarget | null => {
    const pages = state.pages
    if (!pages.some((page) => page.pageNumber == location.pageNumber)) {
      return null
    }

    const transform = selPageTransform(
      selViewerZoomLevel(state.viewer),
      selViewerCssPxPerPdfPt(state.viewer)
    )
    return {
      target: selScrollDocumentLocationRect(pages, transform, location),
      viewportAlignment: location.bounds ? Anchor.center : Anchor.topLeft,
      targetAlignment: location.bounds ? Anchor.center : Anchor.topLeft
    }
  }

  /**
   * External navigation is state-owned work: the pending navigation record
   * holds the document-space target and this Task carries it out against the
   * layout in the committed state. Its identity includes the resolved pixel
   * target, so a layout change before landing (zoom, pages, measurement, a
   * retargeted intent) replaces the session and the runtime suppresses the
   * superseded run's completion. The scroll completes when the viewport lands
   * or the user takes it over; either way the record settles.
   */
  const runNavigation = Task.define(
    (input: {
      key: string
      token: number
      target: ViewportScrollTarget
      behavior: ScrollBehavior
    }) =>
      Effect.andThen(
        viewportScrollToTargetEffect(input.target, input.behavior),
        emitNavigationSettledEffect<Document, PluginsInput>(input.token)
      ),
    { key: (input) => input.key }
  )

  /**
   * The user's own scrolling ends a pending intent: re-issuing the scroll would
   * fight them. This observation is keyed by the intent's token alone, so it
   * outlives the scroll Task, which layout changes replace; a takeover is never
   * lost to a replaced scroll that never reported back.
   */
  const observeNavigationTakeover = Task.define((token: number) =>
    Stream.unwrap(
      Effect.map(Effect.service(ViewportService), (viewport) =>
        viewport.userScrolls.pipe(
          Stream.take(1),
          Stream.map(
            (): ComposedViewerMessage<Document, PluginsInput> => ({
              type: 'navigationTakenOver',
              token
            })
          )
        )
      )
    )
  )

  const selNavigationTakeoverTask = (state: ComposedViewerState<Document, PluginsInput>) => {
    const navigation = state.navigation
    if (navigation?.status != 'pending') return undefined
    return observeNavigationTakeover(navigation.token)
  }

  // Called once pages are laid out. An absolute location passes through: one on
  // a page the document lacks resolves to no scroll target, as it always has. A
  // page region names a region of a page the caller says exists, so a page the
  // laid-out document lacks (deleted pages stay laid out, collapsed) is a caller
  // bug, not a scroll to wait for.
  const resolveDestinationLocation = (
    state: ComposedViewerState<Document, PluginsInput>,
    destination: ViewerDestination
  ): DocumentLocation => {
    if (!('pageRegion' in destination)) return destination
    const page = state.pages.find((candidate) => candidate.pageNumber == destination.pageNumber)
    if (!page) {
      throw new Error(`Page region targets missing page ${destination.pageNumber}`)
    }
    return {
      pageNumber: destination.pageNumber,
      bounds: denormalizeRect(destination.pageRegion, page.bounds)
    }
  }

  const selNavigationTask = (state: ComposedViewerState<Document, PluginsInput>) => {
    const navigation = state.navigation
    if (navigation?.status != 'pending') return undefined
    const location = navigation.location
    if (location == 'awaiting' || location == null) return undefined
    if (!selViewportReadyForNavigation(state)) return undefined
    const target = resolveViewportScrollTarget(state, resolveDestinationLocation(state, location))
    if (!target) return undefined
    const rect = target.target
    return runNavigation({
      key: ['navigation', navigation.token, rect.x, rect.y, rect.width, rect.height].join(':'),
      token: navigation.token,
      target,
      behavior: navigation.behavior
    })
  }

  const selViewportReadyForNavigation = (state: ComposedViewerState<Document, PluginsInput>) => {
    const hasPages = state.pages.length > 0
    const hasNonZeroPageDimensions = state.pages.some(
      (page) => page.bounds.width > 0 && page.bounds.height > 0
    )
    const viewportMeasurement = state.viewer.viewportMeasurement
    const hasNonZeroViewportDimensions =
      viewportMeasurement != null && viewportMeasurement.containerWidthPx > 0

    return (
      state.pagesStatus == 'loaded' &&
      hasPages &&
      hasNonZeroPageDimensions &&
      hasNonZeroViewportDimensions
    )
  }

  const withViewportScrollEffect = (
    state: ComposedViewerState<Document, PluginsInput>,
    message: DocumentViewerMessage
  ) => {
    if (message.type != 'requestViewerScroll') {
      return state
    }
    const target = resolveViewportScrollTarget(state, message.target)
    if (!target) {
      return state
    }
    // A newer direct scroll supersedes any outstanding external navigation. The
    // record settles rather than clearing, so re-presenting its token is a no-op.
    const navigation = state.navigation && settleNavigationRecord(state.navigation)
    return [
      { ...state, navigation },
      viewportScrollToTargetEffect(target, message.behavior)
    ] as const
  }

  const notifyPluginsBeforeSourceChange = (
    state: ComposedViewerState<Document, PluginsInput>,
    context: DocumentViewerPluginRuntimeContext = {}
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > =>
    mapPluginStateUpdate(
      state,
      reducePluginStateMap(state.pluginStates, { type: 'sourceWillChange' }, pluginModules, context)
    )

  const reduceViewerMessage = (
    state: ComposedViewerState<Document, PluginsInput>,
    viewerMessage: DocumentViewerMessage
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > =>
    Update.pipe(
      state,
      (nextState) => {
        if (viewerMessage.type == 'viewerCommand') {
          return [
            nextState,
            Effect.succeed({
              type: 'viewerCommand',
              command: viewerMessage.command
            } as ComposedViewerMessage<Document, PluginsInput>)
          ] as const
        }

        if (viewerMessage.type == 'viewportContainerChanged') {
          return [nextState, bindViewportContainerEffect(viewerMessage.containerId)] as const
        }

        if (viewerMessage.type == 'pageSurfaceMounted') {
          if (nextState.rendererRenderMode != 'bitmap') {
            return nextState
          }
          return reducePageSurfaceMounted(nextState, viewerMessage)
        }

        if (viewerMessage.type == 'pageSurfaceUnmounted') {
          if (nextState.rendererRenderMode != 'bitmap') {
            return nextState
          }
          return reducePageSurfaceUnmounted(nextState, viewerMessage)
        }

        if (viewerMessage.type == 'plugin') {
          return mapPluginStateUpdate(
            nextState,
            reducePluginStateMap(
              nextState.pluginStates,
              viewerMessage,
              pluginModules,
              resolveStateDocumentRuntimeContext(nextState)
            )
          )
        }

        return viewer.update(
          nextState.viewer,
          viewerMessage as DocumentViewerCoreMessage,
          (viewer) => ({ ...nextState, viewer })
        )
      },
      (nextState) => withViewportScrollEffect(nextState, viewerMessage)
    )

  const reduceResolvedPagesChanged = (
    state: ComposedViewerState<Document, PluginsInput>,
    resolvedPages: ResolvedPageStructure[]
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    const rerenderState = clearRenderedAssetsInState({
      ...state,
      pages: resolvedPages,
      pageLayoutVersion: state.pageLayoutVersion + 1
    })

    return Update.pipe(
      reapplyViewportMeasurement(rerenderState),
      (nextState) => reduceViewportStateChanged(nextState, nextState.viewportState),
      Update.effect(() => {
        const sideEffects: Effect.Effect<unknown, never, any>[] = [
          releaseRenderedPageTextEffect({
            documentRendererTag,
            pageRenderStateByPageNumber: state.pageRenderStateByPageNumber
          })
        ]

        if (state.rendererRenderMode == 'bitmap' && state.rendererSessionId) {
          sideEffects.push(setDesiredPagesEffect(documentRendererTag, state.rendererSessionId, []))
        }

        return sequenceSideEffects(sideEffects)
      })
    )
  }

  const reduceSetDocument = (
    state: ComposedViewerState<Document, PluginsInput>,
    document: Document | null
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    const previousDocumentSource =
      state.document == null ? null : resolveSourceFromDocument(state.document)
    const previousDocumentContext = resolveDocumentRuntimeContext(previousDocumentSource)
    const nextDocumentSource = document == null ? null : resolveSourceFromDocument(document)
    const nextDocumentContext = resolveDocumentRuntimeContext(nextDocumentSource)
    const documentSourceChanged = !deepEqual(previousDocumentSource, nextDocumentSource)
    const documentChanged = !deepEqual(state.document, document)
    // A source revision of the same document (same identity) keeps the zoom
    // the viewer settled on: still fitting, the host's initial zoom, or the
    // user's choice. Closing it or opening another document starts over.
    const revisesSameDocument =
      document != null &&
      resolveDocumentIdentity(state.document) == resolveDocumentIdentity(document)
    const resetViewerState = (previous: DocumentViewerState): DocumentViewerState => {
      const initial = viewer.init(undefined as never) as DocumentViewerState
      if (!revisesSameDocument) return initial
      return {
        ...initial,
        viewerShell: { ...initial.viewerShell, zoom: previous.viewerShell.zoom },
        followsFitToWidth: previous.followsFitToWidth
      }
    }
    const reinitializeSourceScopedState = (
      nextState: ComposedViewerState<Document, PluginsInput>,
      context: DocumentViewerPluginRuntimeContext = {}
    ) => {
      const viewportMeasurement = nextState.viewer.viewportMeasurement

      return Update.pipe(
        {
          ...nextState,
          viewer: resetViewerState(nextState.viewer)
        },
        (stateAfterViewerReset) =>
          mapPluginStateUpdate(stateAfterViewerReset, initPluginStates(pluginModules, context)),
        (stateAfterPluginReset) =>
          viewportMeasurement
            ? reduceViewerMessage(
                stateAfterPluginReset,
                toViewportMeasuredMessage(
                  stateAfterPluginReset,
                  viewportMeasurement,
                  viewportInitialZoomMode
                )
              )
            : stateAfterPluginReset,
        (stateAfterPluginReset) => {
          // Closing a viewer must not load preferences: its reply would reach
          // the replacement viewer after the host switches the shared state slot.
          // A revision of the same document keeps its zoom rather than reloading
          // the stored preference.
          if (
            stateAfterPluginReset.document == null ||
            !viewportSettingsKey ||
            revisesSameDocument
          ) {
            return stateAfterPluginReset
          }

          return [
            stateAfterPluginReset,
            requestViewportSettings<Document, PluginsInput>(viewportSettingsKey)
          ] as const
        }
      )
    }

    if (document == null) {
      if (!documentChanged) {
        return state
      }

      const nextState: ComposedViewerState<Document, PluginsInput> = {
        ...resetRendererState({
          ...state,
          document: null,
          rawPages: []
        }),
        pagesStatus: 'loaded',
        pages: [],
        pageLayoutVersion: state.pageLayoutVersion + 1
      }

      return Update.pipe(
        notifyPluginsBeforeSourceChange(state, previousDocumentContext),
        () => reinitializeSourceScopedState(nextState),
        Update.effect(() =>
          releaseRenderedPageTextEffect({
            documentRendererTag,
            pageRenderStateByPageNumber: state.pageRenderStateByPageNumber
          })
        )
      )
    }

    if (documentSourceChanged) {
      return Update.pipe(
        notifyPluginsBeforeSourceChange(state, previousDocumentContext),
        () =>
          reinitializeSourceScopedState(
            {
              ...resetRendererState({
                ...state,
                document,
                rawPages: []
              }),
              pagesStatus: 'loading',
              pages: [],
              pageLayoutVersion: state.pageLayoutVersion + 1
            },
            nextDocumentContext
          ),
        Update.effect(() =>
          releaseRenderedPageTextEffect({
            documentRendererTag,
            pageRenderStateByPageNumber: state.pageRenderStateByPageNumber
          })
        )
      )
    }

    if (!documentChanged) {
      return state
    }

    return Update.pipe<
      ComposedViewerState<Document, PluginsInput>,
      ComposedViewerMessage<Document, PluginsInput>
    >(
      {
        ...state,
        document
      },
      (nextState) => {
        if (state.rawPages.length == 0) {
          return nextState
        }

        const resolvedPages = resolvePagesFromRawPages(state.rawPages, document)
        if (deepEqual(resolvedPages, nextState.pages)) {
          return nextState
        }

        return reduceResolvedPagesChanged(nextState, resolvedPages)
      }
    )
  }

  const reduceMergeAnnotations = (
    state: ComposedViewerState<Document, PluginsInput>,
    annotations: Record<string, unknown>
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    const currentAnnotations =
      (state.pluginStates.annotations as { annotations?: Record<string, unknown> } | undefined)
        ?.annotations ?? {}
    const changes = jsonMapDiff(currentAnnotations, annotations, deepEqual)

    if (Object.keys(changes).length == 0) {
      return state
    }

    return reduceViewerMessage(state, {
      type: 'plugin',
      pluginId: 'annotations',
      message: {
        type: 'remoteAnnotationsUpdated',
        changes
      } as any
    })
  }

  // The host's initial zoom takes ownership of the zoom, so the viewer stops
  // fitting to width. Programmatic zoom does not emit viewer.zoom.changed or
  // persist settings.
  const reduceInitialZoom = (
    state: ComposedViewerState<Document, PluginsInput>,
    zoom: number
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > =>
    reduceViewerMessage(state, {
      type: 'applyViewerZoom',
      level: zoom
    })

  // A host session key names the document across source revisions: new pages
  // or a refreshed URL under the same key are the same document. Without a key,
  // every source is its own document.
  const resolvePresentedSource = (document: Document | null) => {
    if (document == null) return null
    return resolveSourceFromDocument(document)
  }

  const resolveDocumentIdentity = (document: Document | null): string | null => {
    const source = resolvePresentedSource(document)
    if (source == null) return null
    return (
      resolveDocumentRuntimeContext(source).sessionKey ??
      fingerprintDocumentSource(source).toString()
    )
  }

  const selAnnotationsSelectionState = (
    state: ComposedViewerState<Document, PluginsInput>
  ): AnnotationsSelectionState =>
    (state.pluginStates.annotations as { selectionState: AnnotationsSelectionState } | undefined)
      ?.selectionState ?? { type: 'none' }

  const hasPresentedAnnotation = (
    state: ComposedViewerState<Document, PluginsInput>,
    annotationId: string
  ) =>
    (state.pluginStates.annotations as { annotations: Record<string, unknown> } | undefined)
      ?.annotations[annotationId] != undefined

  // Establishes a pending navigation's selection once its annotations are
  // presented. Viewers without annotations have no selection to establish.
  const establishNavigationSelection = (
    state: ComposedViewerState<Document, PluginsInput>
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    const navigation = state.navigation
    if (navigation?.status != 'pending' || navigation.selectionStatus != 'owed') return state
    const selection = navigation.selection
    if (selection == 'keep') throw new Error('A kept selection is never owed')
    if (!pluginModules.annotations) {
      return { ...state, navigation: { ...navigation, selectionStatus: 'done' } }
    }
    if (selection != 'clear' && !hasPresentedAnnotation(state, selection.annotationId)) {
      return state
    }
    const message = selectionMessage(selection)
    return reduceViewerMessage(
      { ...state, navigation: { ...navigation, selectionStatus: 'established' } },
      { type: 'plugin', pluginId: 'annotations', message: message as any }
    )
  }

  const reducePresent = (
    state: ComposedViewerState<Document, PluginsInput>,
    presentation: ViewerPresentation<Document>
  ): UpdateResult<
    ComposedViewerState<Document, PluginsInput>,
    ComposedViewerMessage<Document, PluginsInput>
  > => {
    const identity = resolveDocumentIdentity(presentation.document)
    const identityChanged = identity != resolveDocumentIdentity(state.document)
    const initialZoom = presentation.initialZoom
    // A same-document source revision reinitializes plugin state, dropping the
    // selection a pending navigation established. It is owed again only if the
    // user had left it as established; a selection they changed stays theirs.
    const revisesSource =
      !identityChanged &&
      !deepEqual(
        resolvePresentedSource(state.document),
        resolvePresentedSource(presentation.document)
      )
    const selectionBeforeRevision = selAnnotationsSelectionState(state)
    // The navigation record is reconciled after the document and annotations, so
    // a same-document revision keeps a pending intent and its selection can name
    // annotations presented in this same message, while a different or absent
    // document only keeps the intent the caller presents with it.
    return Update.pipe(
      reduceSetDocument(state, presentation.document),
      (nextState) => {
        if (!identityChanged || initialZoom == undefined) return nextState
        return reduceInitialZoom(nextState, initialZoom)
      },
      (nextState) => reduceMergeAnnotations(nextState, presentation.annotations),
      (nextState) => {
        let navigation = reconcileNavigationRecord(
          nextState.navigation,
          presentation.navigation,
          identity
        )
        if (
          revisesSource &&
          navigation?.token == state.navigation?.token &&
          navigation?.status == 'pending' &&
          navigation.selectionStatus == 'established'
        ) {
          navigation = {
            ...navigation,
            selectionStatus: reestablishedSelectionStatus(
              navigation.selection,
              selectionBeforeRevision
            )
          }
        }
        if (navigation == nextState.navigation) return nextState
        return { ...nextState, navigation }
      },
      establishNavigationSelection,
      (nextState) => {
        // An intent with nothing to scroll settles once its selection step ran.
        const navigation = nextState.navigation
        if (navigation?.status != 'pending' || navigation.location != null) return nextState
        return { ...nextState, navigation: settleNavigationRecord(navigation) }
      }
    )
  }

  const reduceNavigationTakenOver = (
    state: ComposedViewerState<Document, PluginsInput>,
    token: number
  ): ComposedViewerState<Document, PluginsInput> => {
    const navigation = state.navigation
    if (navigation?.token != token || navigation.status != 'pending') return state
    return { ...state, navigation: settleNavigationRecord(navigation) }
  }

  const reduceNavigationSettled = (
    state: ComposedViewerState<Document, PluginsInput>,
    token: number
  ): ComposedViewerState<Document, PluginsInput> => {
    const navigation = state.navigation
    if (navigation?.token != token || navigation.status != 'pending') return state
    if (navigation.location == 'awaiting' || navigation.location == null) {
      throw new Error(`Navigation ${token} settled a scroll it never started`)
    }
    return { ...state, navigation: settleNavigationRecord(navigation) }
  }

  return defModule<{
    state: ComposedViewerState<Document, PluginsInput>
    message: ComposedViewerMessage<Document, PluginsInput>
  }>({
    init: () =>
      Update.pipe<
        ComposedViewerState<Document, PluginsInput>,
        ComposedViewerMessage<Document, PluginsInput>
      >(
        {
          viewer: viewer.init(undefined as never) as DocumentViewerState,
          document: null,
          rawPages: [],
          pagesStatus: 'loaded' as const,
          pages: [] as ResolvedPageStructure[],
          pageLayoutVersion: 0,
          pluginStates: {} as Record<string, unknown>,
          navigation: undefined,
          rendererSessionId: null,
          rendererRenderMode: 'bitmap' as const,
          viewportState: EMPTY_VIEWER_VIEWPORT_STATE,
          pageRenderStateByPageNumber: {}
        },
        (nextState) => mapPluginStateUpdate(nextState, initPluginStates(pluginModules, {})),
        (nextState) => {
          if (!viewportSettingsKey) {
            return nextState
          }

          return Update.pipe(
            nextState,
            Update.effect(() =>
              requestViewportSettings<Document, PluginsInput>(viewportSettingsKey)
            )
          )
        }
      ),
    update: (state, message) => {
      switch (message.type) {
        case 'present':
          return reducePresent(state, message.presentation)
        case 'navigationSettled':
          return reduceNavigationSettled(state, message.token)
        case 'navigationTakenOver':
          return reduceNavigationTakenOver(state, message.token)
        case 'viewer':
          return reduceViewerMessage(state, message.message)
        case 'pluginStates':
          if (message.sessionKey != resolveStateDocumentRuntimeContext(state).sessionKey) {
            return state
          }
          return reduceViewerMessage(state, message.message)
        case 'viewerCommand':
          return state
        case 'viewportMeasured':
          return reduceViewerMessage(
            state,
            toViewportMeasuredMessage(state, message.measurement, viewportInitialZoomMode)
          )
        case 'viewportStateChanged':
          return reduceViewportStateChanged(state, message.viewportState)
        case 'rendererSessionOpened':
          return reduceRendererSessionOpened(state, message)
        case 'rendererSessionOpenFailed':
          return reduceRendererSessionOpenFailed(state, message)
        case 'rendererPageRendered':
          return reduceRendererPageRendered(state, message)
        case 'rendererPageTextExtracted':
          return reduceRendererPageTextExtracted(state, message)
        case 'rendererPageRenderFailed':
          return reduceRendererPageRenderFailed(state, message)
      }
    },
    tasks: (state) => {
      const rendererSessionInput = state.document
        ? resolveRendererSessionInput(state.document)
        : undefined
      const viewportMeasurementKey = [
        'viewport:measured',
        rendererSessionInput?.key ?? 'none',
        state.pagesStatus
      ].join(':')

      const navigation = selNavigationTask(state)
      const takeover = selNavigationTakeoverTask(state)

      return [
        ...(viewer.tasks(state.viewer) as any),
        ...(rendererSessionInput ? [observeRendererSession(rendererSessionInput)] : []),
        observeViewportMeasurements(viewportMeasurementKey),
        observeViewportStates({
          key: resolveViewportStateSubscriptionKey(state),
          pageRects: resolvePageRects(state.pages, selViewerPageTransform(state.viewer))
        }),
        ...(navigation ? [navigation] : []),
        ...(takeover ? [takeover] : [])
      ]
    }
  })
}

export function defDocumentViewerModule<Document, PluginsInput extends Record<string, unknown>>(
  definition: DocumentViewerDefinition<PluginsInput, Document>,
  viewportLayer?: Layer.Layer<ViewportService, never, never>
): Module<
  ComposedViewerState<Document, PluginsInput>,
  ComposedViewerMessage<Document, PluginsInput>
> & {
  layer: Layer.Layer<never, any, RuntimeDefectReporter>
} {
  const viewportInitialZoomMode = definition.viewport?.initialZoomMode ?? 'actualSize'
  const documentRendererTag = definition.source.rendererTag

  const pluginLayers = (definition.plugins ?? [])
    .map((plugin) => plugin.createLayer?.())
    .filter((layer): layer is Layer.Layer<never, any, RuntimeDefectReporter> => !!layer)

  const pluginModules = Object.fromEntries(
    (definition.plugins ?? [])
      .map((plugin) => {
        const module = plugin.createStateModule?.()
        if (!module) return null
        return [plugin.id, module] as const
      })
      .filter(
        (
          entry
        ): entry is readonly [string, DocumentViewerPluginStateModule<unknown, { type: string }>] =>
          !!entry
      )
  )

  const layer = Layer.mergeAll(
    Layer.empty,
    ...(definition.persistence
      ? [definition.persistence.settings, definition.persistence.session]
      : []),
    ...(viewportLayer ? [viewportLayer] : []),
    ...(definition.source.layer ? [definition.source.layer] : []),
    ...pluginLayers
  )

  return {
    ...makeComposedDocumentViewerModule<Document, PluginsInput>({
      documentRendererTag,
      pluginModules,
      viewportInitialZoomMode,
      definition
    }),
    layer
  }
}
